import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';

export interface TaskDetailsData {
  task_id: string;
  status: string;
  date_created: string;
  date_done: string;
  result: string;
  traceback: string | null;
  /** human-readable summary computed by the parent */
  summary: string;
}

@Component({
  selector: 'app-task-details-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  template: `
    <div class="dlg">
      <div class="dlg-h">
        <span class="dlg-ico" [class.ico-ok]="!isFailed">
          <ng-container *ngIf="isFailed">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </ng-container>
          <ng-container *ngIf="!isFailed">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          </ng-container>
        </span>
        <div>
          <div class="dlg-title">Solution run · {{ isFailed ? 'Failed' : 'Completed' }}</div>
          <div class="dlg-sub">
            {{ data.date_created | date : 'MMM d, h:mm a' }} · run #{{ shortId }}
          </div>
        </div>
      </div>

      <div class="dlg-b">
        <div class="kv">
          <span class="k">Summary</span>
          <span [class.fail]="isFailed">{{ data.summary }}</span>
        </div>
        <div class="kv">
          <span class="k">Task ID</span>
          <span class="mono">{{ data.task_id }}</span>
        </div>

        <div class="code" *ngIf="codeBlock">
          <span class="label">{{ codeLabel }}</span>{{ codeBlock }}
        </div>
      </div>

      <div class="dlg-f">
        <button type="button" class="btn-ghost" mat-dialog-close>Close</button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
        color: #1a1a1a;
      }
      ::ng-deep .task-details-dialog .mat-mdc-dialog-surface {
        border-radius: 14px;
        overflow: hidden;
      }
      ::ng-deep .task-details-dialog .mat-mdc-dialog-container {
        --mdc-dialog-container-color: #fff;
      }
      :host ::ng-deep .mat-mdc-dialog-content,
      .dlg {
        padding: 0;
      }
      .dlg-h {
        display: flex;
        gap: 14px;
        align-items: center;
        padding: 18px 22px 12px;
      }
      .dlg-ico {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: #fdecec;
        color: #dc2626;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .dlg-ico.ico-ok {
        background: #e7f6ec;
        color: #16a34a;
      }
      .dlg-title {
        font-size: 17px;
        font-weight: 700;
      }
      .dlg-sub {
        color: #6b7280;
        font-size: 13px;
        margin-top: 1px;
      }
      .dlg-b {
        padding: 4px 22px 18px;
      }
      .kv {
        display: flex;
        gap: 10px;
        font-size: 13px;
        margin-bottom: 10px;
      }
      .kv .k {
        color: #6b7280;
        width: 70px;
        flex-shrink: 0;
      }
      .kv .fail {
        color: #b42318;
      }
      .kv .mono {
        font-family: ui-monospace, Menlo, Consolas, monospace;
        word-break: break-all;
      }
      .code {
        background: #0f172a;
        color: #e2e8f0;
        font-family: ui-monospace, Menlo, Consolas, monospace;
        font-size: 12px;
        border-radius: 9px;
        padding: 13px 14px;
        max-height: 230px;
        overflow: auto;
        white-space: pre;
        line-height: 1.5;
      }
      .code .label {
        color: #94a3b8;
        display: block;
        margin-bottom: 6px;
        font-weight: 600;
      }
      .dlg-f {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 14px 22px;
        border-top: 1px solid #e5e7eb;
        background: #fcfcfd;
      }
      .btn-ghost {
        background: #fff;
        color: #374151;
        border: 1px solid #e5e7eb;
        border-radius: 9px;
        padding: 0 20px;
        height: 38px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        font-family: inherit;
      }
      .btn-ghost:hover {
        background: #f9fafb;
      }
    `,
  ],
})
export class TaskDetailsDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: TaskDetailsData) {}

  get isFailed(): boolean {
    return this.data.status === 'FAILURE';
  }

  get shortId(): string {
    return (this.data.task_id || '').slice(0, 4);
  }

  /** Prefer the traceback; fall back to the raw result string. */
  get codeBlock(): string {
    if (this.data.traceback) {
      return this.data.traceback;
    }
    return this.data.result || '';
  }

  get codeLabel(): string {
    return this.data.traceback
      ? 'TECHNICAL DETAILS (traceback)'
      : 'RAW RESULT';
  }
}
